/**
 * Table Column Sync
 * Синхронизирует ширину столбцов между несколькими одинаковыми таблицами на странице.
 *
 * Использование (декларативно):
 *   <table class="my-table" data-sync-columns="group-name">...</table>
 *   <table class="my-table" data-sync-columns="group-name">...</table>
 *
 * Или программно:
 *   TableColumnSync.sync('.adm_spec_item_table');
 *   TableColumnSync.sync('table[data-sync-columns="users"]');
 *
 * Если у таблиц разное число столбцов — синхронизируются только первые N (минимум).
 */
(function (window) {
    'use strict';

    const debounceTimers = {};

    function getTableNaturalColumnWidths(table) {
        // Снимаем предыдущие <col> чтобы получить естественную ширину контента
        const existingColgroup = table.querySelector('colgroup[data-sync-managed]');
        if (existingColgroup) existingColgroup.remove();

        // Получаем ширины ячеек первой строки <thead> (или первой строки tbody, если нет thead)
        const headRow = table.querySelector('thead tr') || table.querySelector('tbody tr');
        if (!headRow) return [];

        const cells = headRow.children;
        const widths = [];
        for (let i = 0; i < cells.length; i++) {
            // Пропускаем скрытые ячейки: display:none убирает их из table layout,
            // видимые ячейки сдвигаются и иначе попадут не на свой <col>.
            if (window.getComputedStyle(cells[i]).display === 'none') continue;
            widths.push(cells[i].getBoundingClientRect().width);
        }
        return widths;
    }

    function applyColgroup(table, widths) {
        // Удаляем старый managed colgroup
        const existing = table.querySelector('colgroup[data-sync-managed]');
        if (existing) existing.remove();

        const colgroup = document.createElement('colgroup');
        colgroup.setAttribute('data-sync-managed', '1');

        let sum = 0;
        for (let i = 0; i < widths.length; i++) {
            const col = document.createElement('col');
            // Ширина 0 = колонка скрыта (display:none у ячеек) — задаём явно 0px,
            // иначе при table-layout:fixed остаток распределится на скрытую колонку.
            col.style.width = Math.max(0, widths[i]) + 'px';
            sum += Math.max(0, widths[i]);
            colgroup.appendChild(col);
        }

        // colgroup должен быть первым элементом <table>
        table.insertBefore(colgroup, table.firstChild);
        // Жёстко фиксируем layout и ширину таблицы суммой колонок —
        // иначе при table-layout: fixed + width: 100% остаток распределяется по
        // колонкам пропорционально и выравнивание ломается.
        table.style.tableLayout = 'fixed';
        if (sum > 0) {
            table.style.width = sum + 'px';
        }
    }

    function syncGroup(selector) {
        const tables = document.querySelectorAll(selector);
        if (tables.length < 2) return; // Нечего синхронизировать

        // Шаг 1: снять старые colgroup, заставить таблицу растянуться по родителю,
        // собрать естественные ширины колонок.
        const allWidths = [];
        let maxColCount = 0;
        let minParentWidth = Infinity;
        for (let i = 0; i < tables.length; i++) {
            tables[i].style.tableLayout = '';
            tables[i].style.width = '100%'; // не давать таблице раздуваться шире родителя
            const ws = getTableNaturalColumnWidths(tables[i]);
            allWidths.push(ws);
            if (ws.length > maxColCount) maxColCount = ws.length;

            // Доступная ширина = ширина родителя (минимум среди таблиц)
            const pw = tables[i].parentElement ? tables[i].parentElement.getBoundingClientRect().width : 0;
            if (pw > 0 && pw < minParentWidth) minParentWidth = pw;
        }

        if (maxColCount === 0) return;

        // Шаг 2: для каждой колонки (до maxColCount) берём максимум среди тех таблиц,
        // у которых эта колонка есть. Таблица с большим числом колонок задаёт эталон
        // для лишних колонок; первые N колонок выравниваются по самой широкой версии.
        const maxWidths = [];
        for (let c = 0; c < maxColCount; c++) {
            let max = 0;
            for (let t = 0; t < allWidths.length; t++) {
                if (allWidths[t][c] !== undefined && allWidths[t][c] > max) max = allWidths[t][c];
            }
            maxWidths.push(max);
        }

        // Шаг 3: если сумма ширин превышает доступную ширину контейнера —
        // пропорционально уменьшим все колонки, чтобы таблица влезла.
        if (minParentWidth !== Infinity && minParentWidth > 0) {
            const sum = maxWidths.reduce(function (s, w) { return s + w; }, 0);
            if (sum > minParentWidth) {
                const scale = minParentWidth / sum;
                for (let m = 0; m < maxWidths.length; m++) {
                    maxWidths[m] = Math.floor(maxWidths[m] * scale);
                }
            }
        }

        // Шаг 4: применить colgroup. Меньшим таблицам отдаём только первые N ширин
        // (по числу реально существующих в них колонок).
        for (let k = 0; k < tables.length; k++) {
            const widths = maxWidths.slice(0, allWidths[k].length);
            applyColgroup(tables[k], widths);
        }
    }

    function debouncedSync(selector, delay) {
        if (debounceTimers[selector]) clearTimeout(debounceTimers[selector]);
        debounceTimers[selector] = setTimeout(function () {
            syncGroup(selector);
        }, delay || 100);
    }

    const TableColumnSync = {
        sync: syncGroup,

        // Подключить автоматическую синхронизацию для группы
        // Слушает resize окна и пересинхронизирует
        watch: function (selector) {
            // Дать браузеру дорисовать
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', function () { syncGroup(selector); });
            } else {
                // requestAnimationFrame чтобы дождаться layout
                if (window.requestAnimationFrame) {
                    requestAnimationFrame(function () { syncGroup(selector); });
                } else {
                    syncGroup(selector);
                }
            }
            window.addEventListener('resize', function () {
                debouncedSync(selector, 150);
            });
        }
    };

    window.TableColumnSync = TableColumnSync;

    // Декларативная инициализация: если на странице есть таблицы с data-sync-columns,
    // автоматически синхронизировать их по группам
    function autoInit() {
        const marked = document.querySelectorAll('table[data-sync-columns]');
        const groups = {};
        for (let i = 0; i < marked.length; i++) {
            const group = marked[i].getAttribute('data-sync-columns');
            if (!groups[group]) groups[group] = true;
        }
        Object.keys(groups).forEach(function (group) {
            TableColumnSync.watch('table[data-sync-columns="' + group + '"]');
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', autoInit);
    } else {
        autoInit();
    }
})(window);
